/**
 * Resilience Coordinator - 유일한 복구 진입점
 *
 * ISSUE_045: Data-Driven 복원력 시스템 조율
 * ISSUE_073: 단순화 — Coordinator가 유일한 복구 진입점
 *
 * 역할:
 * - HealthMonitor 통합 관리
 * - 이벤트 기반 복구 로직 실행 (유일한 복구 진입점)
 * - Circuit Breaker 상태에 따른 동작 제어
 * - 사용자 알림 조율
 * - 탭 복귀 복구 (RealtimeManager에서 이관)
 *
 * 복구 시퀀스: refreshToken → resetChannels → sync (setAuth는 SDK 자동 처리)
 *
 * @module core/resilience/ResilienceCoordinator
 */

import { isSupabaseConfigured, supabase } from '~/shared/lib/supabase';
import { persistentToast } from '~/core/utils/persistentToast';
import { useResilienceStore } from './store';
import { useHealthStore, type HealthStatus } from './healthStore';
import { healthMonitor, startHealthMonitor, stopHealthMonitor, type HealthEventType } from './HealthMonitor';
import { getCircuitBreaker } from './CircuitBreaker';
import { triggerSyncWorkerSync, notifySyncWorkerAuthDead } from '~/core/sync/workerClient';
import { refreshAuthToken, initializeAuthBroker, cleanupAuthBroker, authBroker } from '~/core/auth/AuthBroker';
import { initializeRealtimeManager, stopRealtimeManager, resetAllRealtimeChannels, realtimeManager } from '~/core/realtime/RealtimeManager';
import { resilienceReporter } from './ResilienceReporter';

// =============================================================================
// TYPES
// =============================================================================

export type RecoveryReason =
  | 'health_disconnected'
  | 'visibility_return'
  | 'channel_error'
  | 'token_refresh_failed'
  | 'ws_stale'
  | 'manual';

// =============================================================================
// CONSTANTS
// =============================================================================

/** 복구 타임아웃 (30초) */
const RECOVERY_TIMEOUT = 30_000;

/** 탭 복귀 시 AuthBroker proactiveRefresh 대기 시간 (8초) */

/** reason별 쿨다운 */
const REASON_COOLDOWNS: Record<RecoveryReason, number> = {
  health_disconnected: 60_000,
  visibility_return: 60_000,
  channel_error: 60_000,
  token_refresh_failed: 60_000,
  ws_stale: 90_000,
  manual: 30_000,
};

// =============================================================================
// COORDINATOR
// =============================================================================

class ResilienceCoordinator {
  private isRunning = false;
  private isRecovering = false;
  private unsubscribers: Array<() => void> = [];
  private lastNotificationAt: number | null = null;

  /** 알림 쿨다운 (30초) */
  private readonly NOTIFICATION_COOLDOWN = 30 * 1000;

  /** ISSUE_073: 탭 복귀 복구 타이머 */

  /** ISSUE_073: 탭 복귀 대기 플래그 — HealthMonitor touchQueryOk와 독립 판단 */
  private pendingVisibilityRecovery = false;

  /** ISSUE_073: 복구 직후 channel_error 무시 grace period */
  private lastRecoveryAt = 0;
  private readonly CHANNEL_ERROR_GRACE_MS = 10 * 1000;

  /** ISSUE_073: reason별 마지막 트리거 시각 (rate limit용) */
  private lastTriggerByReason: Partial<Record<RecoveryReason, number>> = {};

  /** ISSUE_073: 탭 숨김 시각 — 복귀 시 hidden duration 계산용 */
  private lastHiddenAt: number = Date.now();

  /** 탭 숨김 최소 시간 — full recovery 기준 (60초) */
  private readonly MIN_HIDDEN_FOR_FULL_RECOVERY_MS = 60 * 1000;

  /** 탭 숨김 최소 시간 — 채널 리셋 기준 (5초, 브라우저 WS throttle 대응) */
  private readonly MIN_HIDDEN_FOR_CHANNEL_RESET_MS = 5 * 1000;

  /** 탭 숨김 시 WS 선제 끊기 타이머 */
  private hiddenDisconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * 복원력 시스템 시작
   */
  start(): void {
    if (this.isRunning) {
      return;
    }

    if (!isSupabaseConfigured()) {
      return;
    }

    this.isRunning = true;

    // 1. Initialize core components
    initializeAuthBroker();

    // 2. 하위 시스템 시작 (ConnectionManager 제거)
    startHealthMonitor();
    initializeRealtimeManager();

    // 3. 이벤트 구독
    this.setupEventHandlers();

    // 4. 상태 변경 구독
    this.setupStoreSubscriptions();

    // 5. AuthBroker 이벤트
    this.setupAuthBrokerListener();

    // 6. RealtimeManager 이벤트 (채널 리셋 후 sync + 채널 에러 복구)
    this.setupRealtimeManagerListener();

    // 7. 탭 복귀 추적
    this.setupVisibilityHandler();

    // 8. ISSUE_155: online 이벤트에서 Reporter 윈도우 오픈
    this.setupOnlineHandler();
  }

  /**
   * 복원력 시스템 중지
   */
  stop(): void {
    if (!this.isRunning) return;

    // 구독 해제
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];

    // 하위 시스템 중지
    cleanupAuthBroker();
    stopHealthMonitor();
    stopRealtimeManager();

    if (this.hiddenDisconnectTimer) {
      clearTimeout(this.hiddenDisconnectTimer);
      this.hiddenDisconnectTimer = null;
    }

    this.isRunning = false;
    this.isRecovering = false;
    this.pendingVisibilityRecovery = false;
    this.lastTriggerByReason = {};
  }

  /**
   * 전체 복구 시도 — 유일한 복구 진입점
   * ISSUE_073: offline 가드 + isRecovering 가드 + reason별 rate limit + 30초 타임아웃
   *
   * 시퀀스: refreshToken → resetChannels → sync
   */
  async performFullRecovery(reason: RecoveryReason = 'manual'): Promise<boolean> {
    // 0-a. Auth dead 가드 — 복구 불능 상태면 즉시 반환
    if (authBroker.getLiveness() === 'dead') return false;

    // 0. 백그라운드 가드 — 탭이 숨겨진 상태에서는 복구하지 않음
    // 브라우저가 WS를 스로틀하므로 채널 타임아웃은 정상. 탭 복귀 시 복구.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && reason !== 'manual') {
      return false;
    }

    // 1. offline 가드
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }

    // 2. 재진입 방지
    if (this.isRecovering) {
      return false;
    }

    // 3. reason별 rate limit
    if (this.isRateLimited(reason)) {
      return false;
    }

    // 4. 글로벌 쿨다운 — 직전 복구 성공 후 60초간 모든 복구 차단
    const timeSinceLastRecovery = Date.now() - this.lastRecoveryAt;
    if (this.lastRecoveryAt > 0 && timeSinceLastRecovery < 60_000) {
      return true;
    }

    this.lastTriggerByReason[reason] = Date.now();
    this.isRecovering = true;

    // F1: doRecovery 완료 시에만 isRecovering 리셋.
    // 타임아웃은 반환값만 false로 — isRecovering은 유지하여 doRecovery 자연 종료까지 새 복구 차단.
    // M1: 성공 시 타임아웃 타이머 정리 — 허위 에러 로그 방지
    let timeoutId: ReturnType<typeof setTimeout>;
    const recoveryPromise = this.doRecovery().then(
      (result) => { this.isRecovering = false; return result; },
      (err) => { this.isRecovering = false; console.error('[Coordinator] doRecovery threw:', err); return false; },
    );
    const timeoutPromise = new Promise<boolean>((resolve) => {
      timeoutId = setTimeout(() => {
        console.error('[Coordinator] Recovery timed out after 30s');
        resolve(false);
      }, RECOVERY_TIMEOUT);
    });

    return Promise.race([
      recoveryPromise.finally(() => clearTimeout(timeoutId!)),
      timeoutPromise,
    ]);
  }

  /**
   * 토큰 갱신
   */
  async refreshToken(): Promise<boolean> {
    try {
      const { token } = await refreshAuthToken();

      if (token) {
        useResilienceStore.getState().incrementTokenRefreshes();
        useHealthStore.getState().touchQueryOk();
        return true;
      }

      console.error('[Coordinator] Token refresh returned null');
      return false;
    } catch (error) {
      console.error('[Coordinator] Token refresh failed:', error);
      return false;
    }
  }

  // ===========================================================================
  // PRIVATE: RECOVERY
  // ===========================================================================

  private isRateLimited(reason: RecoveryReason): boolean {
    const cooldown = REASON_COOLDOWNS[reason];
    const lastAt = this.lastTriggerByReason[reason] ?? 0;
    return Date.now() - lastAt < cooldown;
  }

  /**
   * 실제 복구 본체
   * 시퀀스: 1. refreshToken → 2. resetChannels → 3. sync
   */
  private async doRecovery(): Promise<boolean> {
    const circuitBreaker = getCircuitBreaker('recovery');

    if (circuitBreaker.isOpen()) {
      this.showNotification('error', '복구를 너무 자주 시도했습니다', '잠시 후 다시 시도해주세요');
      return false;
    }

    try {
      // 1. Token refresh
      const { token } = await refreshAuthToken();

      if (!token) {
        // Auth dead면 circuit breaker 기록 생략 — forceLogout이 처리
        if (authBroker.getLiveness() === 'dead') return false;
        circuitBreaker.recordFailure();
        console.error('[Coordinator] Token refresh returned null');
        return false;
      }

      // 2. Reset realtime channels
      // Note: setAuth는 SDK가 TOKEN_REFRESHED 이벤트 시 자동 호출 (supabase-js _handleTokenChanged)
      await resetAllRealtimeChannels('recovery');

      // ISSUE_073: heartbeat 채널도 재생성 (resetAllRealtimeChannels가 WS를 리셋하면 파괴됨)
      healthMonitor.restartHeartbeat();

      // 3. 동기화 재시도
      await triggerSyncWorkerSync();

      // Update health store
      useHealthStore.getState().touchSyncOk();
      circuitBreaker.recordSuccess();
      this.lastRecoveryAt = Date.now();

      // ISSUE_155: isRecovering 가드 때문에 AuthBroker listener 에서 markAuthRecovered 가
      // 호출되지 않을 수 있음. doRecovery 성공 경로에서 명시적으로 마킹.
      resilienceReporter.markAuthRecovered();

      return true;
    } catch (err) {
      console.error('[Coordinator] Full recovery failed:', err);
      circuitBreaker.recordFailure();
      return false;
    }
  }

  // ===========================================================================
  // PRIVATE: EVENT SETUP
  // ===========================================================================

  /**
   * HealthMonitor 이벤트 핸들러 설정
   */
  private setupEventHandlers(): void {
    const unsubHealth = healthMonitor.onEvent((event) => {
      this.handleHealthEvent(event);
    });

    this.unsubscribers.push(unsubHealth);
  }

  /**
   * AuthBroker 이벤트 리스너 설정
   * ISSUE_073: realtime_auth_failed 제거, token_refreshed 시 복구 판단
   */
  private setupAuthBrokerListener(): void {
    const unsubAuthBroker = authBroker.onEvent((event) => {
      if (event.type === 'auth_dead') {
        // Auth 복구 불능 — Worker 타이머 정지
        notifySyncWorkerAuthDead();
        return;
      }

      if (event.type === 'token_refreshed') {
        // ISSUE_155: 복원 윈도우 중이면 auth 복귀 마킹 (isRecovering 분기 전에)
        resilienceReporter.markAuthRecovered();

        // F8: 복구 중 자기 refreshToken이 emit한 이벤트 무시 — 불필요한 로그/호출 방지
        if (this.isRecovering) {
          return;
        }

        useHealthStore.getState().touchQueryOk();

        // ISSUE_073: 탭 복귀 대기 중 토큰 갱신 수신 → 타이머 취소 + 채널 리셋
        // HealthMonitor가 touchQueryOk로 status를 connected로 리셋하므로
        // health status와 무관하게 pendingVisibilityRecovery 플래그로 판단
        if (this.pendingVisibilityRecovery) {
          this.pendingVisibilityRecovery = false;
          void resetAllRealtimeChannels('tab_visible_token_refreshed');
          return;
        }

        // 탭 복귀가 아닌 일반 상황: degraded/disconnected면 전체 복구
        const healthStatus = useHealthStore.getState().status;
        if (healthStatus === 'degraded' || healthStatus === 'disconnected') {
          void this.performFullRecovery('token_refresh_failed');
        }
      } else if (event.type === 'refresh_failed') {
        // ISSUE_073/155/164 정책: refresh_failed 무반응 분기는 의도된 설계.
        //
        // 책임 분담:
        //   - AuthBroker.refreshToken() — consecutiveFailures>=3 또는
        //     refresh_token_expired 시 forceLogout() 호출 (실제 액션의 주체).
        //   - resilienceReporter — refresh_failed 이벤트를 transient/null 보고 →
        //     Sentry breadcrumb 으로 축적 (관측의 주체).
        //   - Coordinator — visibility resume 대기 컨텍스트에서만 채널 리셋
        //     fallback 만 담당. 그 외 컨텍스트에서는 무반응이 정답.
        //
        // 즉 pendingVisibilityRecovery=false 일 때 아무것도 안 하는 것은
        // 누락이 아니라 위임. git blame: cca68 (ISSUE_155) 에서 console.error
        // 제거가 동시에 이뤄짐 — Reporter 로 단일화. ISSUE_164 에서 주석으로 박제.
        if (this.pendingVisibilityRecovery) {
          this.pendingVisibilityRecovery = false;
          void resetAllRealtimeChannels('tab_visible_refresh_failed');
          return;
        }
      }
    });

    this.unsubscribers.push(unsubAuthBroker);
  }

  /**
   * RealtimeManager 이벤트 리스너
   * ISSUE_073: 채널 리셋 후 sync + 채널 에러/타임아웃 시 복구
   */
  private setupRealtimeManagerListener(): void {
    const unsubRealtime = realtimeManager.onEvent((event) => {
      if (event.type === 'channel_subscribed') {
        // ISSUE_155: 채널 재구독 성공 → Reporter 에 복귀 마킹 (윈도우 auto-close 판정 트리거)
        if (event.channelName) {
          resilienceReporter.markChannelRecovered(event.channelName);
        }
        return;
      }

      if (event.type === 'all_channels_reset') {
        // F3: recovery reason이면 sync 건너뛰기 — doRecovery가 직접 sync 호출
        if (event.reason === 'recovery') {
          return;
        }
        void triggerSyncWorkerSync();
      } else if (event.type === 'channel_error') {
        // ISSUE_155: Reporter 에 신고 — 윈도우 중이면 버퍼, 아니면 즉시 error
        if (event.channelName) {
          resilienceReporter.report({ kind: 'channel_error', detail: event.channelName });
        }

        // 복구 직후 10초간은 채널 에러 무시 — 재생성 채널이 다시 에러나면 연쇄 루프 방지
        const timeSinceRecovery = Date.now() - this.lastRecoveryAt;
        if (timeSinceRecovery < this.CHANNEL_ERROR_GRACE_MS) {
          return;
        }
        void this.performFullRecovery('channel_error');
      }
    });
    this.unsubscribers.push(unsubRealtime);
  }

  /**
   * ISSUE_073: 탭 복귀 시 복구 스케줄링
   * AuthBroker proactiveRefresh가 8초 내 토큰 갱신 안 하면 직접 복구
   */
  private setupVisibilityHandler(): void {
    if (typeof document === 'undefined') return;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        this.lastHiddenAt = Date.now();

        // SDK 재연결 루프 스팸 방지: 5초 후 WS 선제 끊기
        // 짧은 탭 전환 시에는 타이머 취소되어 영향 없음
        if (!this.hiddenDisconnectTimer) {
          this.hiddenDisconnectTimer = setTimeout(() => {
            this.hiddenDisconnectTimer = null;
            supabase?.realtime.disconnect();
          }, this.MIN_HIDDEN_FOR_CHANNEL_RESET_MS);
        }
        return;
      }

      // visible — 선제 끊기 타이머 취소 (5초 이내 복귀 시)
      if (this.hiddenDisconnectTimer) {
        clearTimeout(this.hiddenDisconnectTimer);
        this.hiddenDisconnectTimer = null;
      }

      const hiddenDuration = Date.now() - this.lastHiddenAt;

      // 5초 미만: 무시 (즉각 탭 전환)
      if (hiddenDuration < this.MIN_HIDDEN_FOR_CHANNEL_RESET_MS) {
        return;
      }

      // 5~60초: 채널 리셋만 (브라우저가 WS를 끊었을 수 있음)
      if (hiddenDuration < this.MIN_HIDDEN_FOR_FULL_RECOVERY_MS) {
        // ISSUE_155: 복원 윈도우 오픈 — 직후 발생할 channel_error 를 버퍼링
        resilienceReporter.openWindow('tab_resume_short');
        void resetAllRealtimeChannels('tab_visible_short');
        return;
      }

      // 60초 이상: full recovery 스케줄
      // ISSUE_155: 복원 윈도우 오픈 — full recovery 과정의 channel_error + auth_refresh 를 버퍼링
      resilienceReporter.openWindow('tab_resume_long');
      this.scheduleVisibilityRecovery();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    this.unsubscribers.push(() => document.removeEventListener('visibilitychange', handleVisibility));
  }

  /**
   * ISSUE_073: 탭 복귀 안전장치
   * 타이머 대신 이벤트 기반: AuthBroker가 token_refreshed 또는 refresh_failed를 보내면 반응.
   * AuthBroker proactiveRefresh(최대 14초)와 경합하지 않음.
   */
  private scheduleVisibilityRecovery(): void {
    this.pendingVisibilityRecovery = true;
    // 타이머 없음 — AuthBroker 이벤트(token_refreshed/refresh_failed) 핸들러가 처리
  }

  /**
   * ISSUE_155: navigator online 이벤트에서 Reporter 윈도우 오픈
   * 네트워크 복구 직후 터지는 channel_error/refresh_timeout 을 버퍼링.
   */
  private setupOnlineHandler(): void {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      resilienceReporter.openWindow('online_event');
    };
    window.addEventListener('online', handleOnline);
    this.unsubscribers.push(() => window.removeEventListener('online', handleOnline));
  }

  /**
   * Store 상태 변경 구독
   */
  private setupStoreSubscriptions(): void {
    const unsubHealth = useHealthStore.subscribe(
      (state) => state.status,
      (status, prevStatus) => {
        if (status !== prevStatus) {
          this.handleHealthStatusChange(status, prevStatus);
        }
      }
    );

    const unsubCircuit = useResilienceStore.subscribe(
      (state) => state.circuitState,
      (state, prevState) => {
        if (state === 'open' && prevState !== 'open') {
          this.handleCircuitOpened();
        }
      }
    );

    this.unsubscribers.push(unsubHealth, unsubCircuit);
  }

  // ===========================================================================
  // PRIVATE: EVENT HANDLERS
  // ===========================================================================

  /**
   * Health 이벤트 처리
   */
  private handleHealthEvent(event: {
    type: HealthEventType;
    from?: HealthStatus;
    to?: HealthStatus;
    details?: string;
  }): void {
    switch (event.type) {
      case 'connected_restored':
        this.handleConnectionRestored();
        break;

      case 'degraded_entered':
        break;

      case 'disconnected_entered':
        void this.performFullRecovery('health_disconnected');
        break;

      case 'ws_stale':
        void this.performFullRecovery('ws_stale');
        break;

      case 'status_changed':
        break;
    }
  }

  private handleConnectionRestored(): void {
    void triggerSyncWorkerSync();
  }

  private handleHealthStatusChange(_status: HealthStatus, _prevStatus: HealthStatus): void {
    // R2: connected_restored 이벤트 핸들러가 이미 복원 처리 — 중복 호출 제거
  }

  private handleCircuitOpened(): void {
    // Circuit breaker opened — auto-retry scheduled
  }

  /**
   * 알림 표시 (쿨다운 적용)
   */
  private showNotification(
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    description?: string,
    showRetry = false
  ): void {
    if (this.lastNotificationAt) {
      const timeSinceLast = Date.now() - this.lastNotificationAt;
      if (timeSinceLast < this.NOTIFICATION_COOLDOWN) {
        return;
      }
    }

    this.lastNotificationAt = Date.now();

    persistentToast(type, title, {
      ...(description !== undefined && { description }),
      ...(showRetry && { action: { label: '새로고침', onClick: () => window.location.reload() } }),
    });
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const resilienceCoordinator = new ResilienceCoordinator();

export function startResilienceSystem(): void {
  resilienceCoordinator.start();
}

export function stopResilienceSystem(): void {
  resilienceCoordinator.stop();
}

export function performFullRecovery(reason: RecoveryReason = 'manual'): Promise<boolean> {
  return resilienceCoordinator.performFullRecovery(reason);
}

export function refreshToken(): Promise<boolean> {
  return resilienceCoordinator.refreshToken();
}
